import { yupResolver } from '@hookform/resolvers/yup'
import { useDisabledPromoCodesList } from '@if/core'
import { compareAsc } from '@if/core/date'
import {
  EFinancingProgram,
  EPaymentFrequency,
  EPaymentPlan,
  EPlanType,
  EProvince,
  EWorksheetStatus,
} from '@if/core/types'
import { formatCurrency, formatDate, normalizeNumber } from '@if/core/utils'
import { ControlledStringDateOnlyPicker } from '@if/mui'
import { ErrorOutline } from '@mui/icons-material'
import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, Stack, Typography, useTheme } from '@mui/material'
import { Breadcrumb, InputTextField, PageError, PageSpinner, SelectComponent } from '@src/components'
import MerchantPaymentInstructions from '@src/containers/ViewCreditApplicationPage/components/MerchantPaymentInstructions'
import PrivateMessage from '@src/containers/ViewCreditApplicationPage/components/privateMessage'
import {
  canComputeFunding,
  getEmptyComputedFundingDto,
  getHighCostRateForProvince,
} from '@src/containers/ViewWorksheet/viewWorksheet-selectors'
import { TRootState, useAppDispatch, useAppSelector } from '@src/data/store'
import { appActions, appSelectors } from '@src/data/store/AppStore'
import availableCreditEffects from '@src/data/store/AvailableCredit/available-credit-effects'
import { availableCreditSelectors } from '@src/data/store/AvailableCredit/available-credit-store'
import { creditSelectors } from '@src/data/store/CreditApplication'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import { fullCreditApplicationEffects } from '@src/data/store/FullCreditApplication'
import ifXpressWorksheetEffects from '@src/data/store/IfXpressWorksheet/ifxpress-worksheet-effects'
import {
  ifXpressWorksheetActions,
  ifXpressWorksheetSelectors,
} from '@src/data/store/IfXpressWorksheet/ifxpress-worksheet-store'
import { messageEffects, messageSelectors } from '@src/data/store/Message'
import { userSelectors } from '@src/data/store/UserStore'
import { CreditApplicationMessage } from '@src/data/types'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { buildEditIfXpressWorksheetDtoSchema, EditIfXpressWorksheetDto } from '@src/data/types/IfXpressWorksheetSchema'
import { reportErrorToConsole } from '@src/services/error-logger'
import { formatPaymentPlanForSummary } from '@src/services/Formatter'
import { debounce } from 'lodash-es'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import AvailableCreditTooltip from '../../../components/AvailableCreditTooltip'
import {
  aprThresholds,
  useAprTooHigh,
  useFirstPaymentDateOptions,
  usePaymentPlanList,
  usePossibleTerms,
} from '../editWorksheet-hooks'

const EditIfXpressWorksheetPage = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()

  const creditApplication = useAppSelector(creditSelectors.getCurrent)
  const financingConfig = useAppSelector((state: TRootState) =>
    appSelectors.getFinancingConfig(state, creditApplication?.financingProgramId),
  )

  const worksheetYupResolver = useMemo(
    () => yupResolver(buildEditIfXpressWorksheetDtoSchema(financingConfig)),
    [financingConfig],
  )

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { errors },
  } = useForm<EditIfXpressWorksheetDto>({
    mode: 'onBlur',
    defaultValues: {} as EditIfXpressWorksheetDto,
    resolver: worksheetYupResolver,
    context: { loanPurposeId: creditApplication?.loanPurposeId },
  })

  const dispatch = useAppDispatch()
  const dispatchEffect = useSideEffect()
  const theme = useTheme()
  const availableCreditData = useAppSelector(availableCreditSelectors.get)
  const availableCredit = useAppSelector(availableCreditSelectors.getAvailableCredit)
  const creditInProgress = useAppSelector(availableCreditSelectors.getCreditInProgress)
  const isLoading = useAppSelector(ifXpressWorksheetSelectors.isSavingIfXpressWorksheet)
  const isLoadingCreditApplication = useAppSelector(creditSelectors.isLoadingCreditApplication)
  const isComputingFunding = useAppSelector(ifXpressWorksheetSelectors.isComputeIfXpressFunding)
  const error = useAppSelector(appSelectors.getBusinessError)
  const merchant = useAppSelector(creditSelectors.getMerchant)
  const ifXpressWorksheet = useAppSelector(ifXpressWorksheetSelectors.getCurrent)
  const computedInfo = useAppSelector(ifXpressWorksheetSelectors.getComputedInfo)
  const totalFees = useAppSelector(ifXpressWorksheetSelectors.getTotalfees)
  const totalInsurance = useAppSelector(ifXpressWorksheetSelectors.getTotalInsurance)
  const disabledPromoList = useDisabledPromoCodesList(
    merchant?.planPromoRules,
    creditApplication?.normsCreditDecision,
    creditApplication?.financingProgramId ?? EFinancingProgram.Personal,
  )
  const paymentPlanList = usePaymentPlanList(
    creditApplication?.createdOn,
    merchant?.paymentPlans,
    disabledPromoList,
    ifXpressWorksheet?.paymentPlanId,
  )

  const aprTooHigh = useAprTooHigh(computedInfo, creditApplication)
  const privateMessages = useAppSelector(messageSelectors.getPrivateMessages)
  const user = useAppSelector(userSelectors.getUser)
  const firstPaymentOn = watch('firstPaymentOn')
  const amountRequested = normalizeNumber(watch('amountRequested')?.toString()) ?? 0
  const [isVariableInterest, setIsVariableInterest] = useState(false)

  const possibleTerms = usePossibleTerms(
    creditApplication?.finalCreditDecision.maxTermDuration,
    isVariableInterest,
    financingConfig,
    amountRequested,
  )
  const provinceConfigs = useAppSelector(appSelectors.getProvinceConfigurations)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const easternTime = new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' })
  const currentDateAsEasternTimeZone = new Date(easternTime)

  useEffect(() => {
    if (amountRequested) trigger('term').catch(reportErrorToConsole)
  }, [amountRequested, trigger])

  // dynamic values
  const paymentFrequency = watch('paymentFrequency') as EPaymentFrequency
  const paymentPlanId = watch('paymentPlanId') as EPaymentPlan
  const deliveryOn = watch('deliveryOn')
  const term = watch('term')
  const includeInsurance = watch('includeInsurance')

  const isNFl =
    creditApplication?.applicant.currentAddress.stateIso &&
    creditApplication.applicant.currentAddress.stateIso === EProvince.Newfoundland

  const highCostRateForProvince = getHighCostRateForProvince(
    provinceConfigs,
    creditApplication?.applicant.currentAddress.stateIso,
  )
  const totalObligation = amountRequested + (computedInfo?.totalInterestAmount ?? 0) + totalFees + totalInsurance
  const planName = creditApplication?.normsCreditDecision?.planName ?? EPlanType.Default

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.IfXpress}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const selectedPlan = useMemo(
    () => merchant?.paymentPlans?.find((p) => p.id === paymentPlanId),
    [merchant?.paymentPlans, paymentPlanId],
  )

  useEffect(() => {
    if (creditApplication?.id) {
      dispatchEffect(
        availableCreditEffects.getAvailableCredit(creditApplication.id, creditApplication.financingProgramId),
      ).catch(reportErrorToConsole)
    }
  }, [creditApplication, dispatchEffect])

  // reset form on worksheet changed
  useEffect(() => {
    if (ifXpressWorksheet && merchant) {
      reset(ifXpressWorksheet as EditIfXpressWorksheetDto)
      setValue('firstPaymentOn', ifXpressWorksheet.firstPaymentOn ?? formatDate(new Date()))
    }
  }, [ifXpressWorksheet, merchant, reset, setValue])

  const computeFunding = useCallback(
    (computeDto: FundingInfoDto) => {
      return dispatchEffect(ifXpressWorksheetEffects.computeIfXpressFunding(computeDto))
    },
    [dispatchEffect],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceComputeFunding = useCallback(debounce(computeFunding, 500), [computeFunding])

  // recompute funding
  useEffect(() => {
    const interestRate = creditApplication?.finalCreditDecision.interestRate ?? 0
    const hasCoApplicant = creditApplication?.coApplicant !== null
    const stateIso = creditApplication?.applicant.currentAddress.stateIso
    const requestId = nanoid()
    if (
      amountRequested >= financingConfig.minimumLoanAmount &&
      paymentFrequency &&
      term &&
      deliveryOn &&
      paymentPlanId &&
      firstPaymentOn &&
      stateIso &&
      ifXpressWorksheet &&
      merchant
    ) {
      const computeDto: FundingInfoDto = {
        financingProgramId: creditApplication?.financingProgramId,
        amountRequested,
        includeInsurance,
        insuranceCompanyId: ifXpressWorksheet.insuranceCompanyId,
        paymentFrequency,
        paymentPlanId,
        term,
        interestRate,
        hasCoApplicant,
        stateIso,
        deliveryOn,
        firstPaymentOn,
        requestId,
        creditApplicationId: creditApplication.id,
        merchantId: merchant.id,
      }

      if (canComputeFunding(computeDto, financingConfig.minimumLoanAmount))
        debounceComputeFunding(computeDto)?.catch(reportErrorToConsole)
      else {
        const emptyComputedDto = getEmptyComputedFundingDto()

        dispatch(ifXpressWorksheetActions.setFundingComputed(emptyComputedDto))
      }
    }
  }, [
    amountRequested,
    paymentFrequency,
    paymentPlanId,
    deliveryOn,
    firstPaymentOn,
    term,
    includeInsurance,
    creditApplication,
    dispatch,
    id,
    debounceComputeFunding,
    merchant?.id,
    merchant,
    ifXpressWorksheet,
    financingConfig.minimumLoanAmount,
  ])

  useEffect(() => {
    const isVariable = Boolean(paymentPlanId && paymentPlanId !== EPaymentPlan.RegularDailyInterests)

    if (isVariable && selectedPlan) {
      setValue('term', selectedPlan.loanTerm)
    }

    setIsVariableInterest(isVariable)
  }, [paymentPlanId, selectedPlan, selectedPlan?.loanTerm, setValue])

  const getMerchantFees = () => {
    if (selectedPlan) return formatCurrency((selectedPlan.merchantFeeRate / 100) * amountRequested)
    return ''
  }

  const handleSave = async () => {
    const formData = getValues()
    await onFormSubmitted(formData)
  }

  const onFormSubmitted = async (data: EditIfXpressWorksheetDto) => {
    setIsSubmitting(true)
    if (isDialogOpen) {
      setIsDialogOpen(false)
    }
    const expiry = new Date(creditApplication!.expiresOn)
    const hardHitReport = creditApplication!.applicant.hardHitReport || creditApplication!.coApplicant?.hardHitReport
    const receivedOnPlus90Days = new Date(hardHitReport.receivedOn)
    receivedOnPlus90Days.setDate(receivedOnPlus90Days.getDate() + 90)
    const deliveryOnDate = new Date(`${getValues('deliveryOn')}T20:00:00`) // 20PM Local Time
    const deliveryOnUTC = new Date(`${getValues('deliveryOn')}T00:00:00`)
    const deliveryOnDateAsEasternTimeZone = new Date(
      deliveryOnDate.toLocaleString('en-US', { timeZone: 'America/Toronto' }),
    )
    const baseActivationDateAsEasternTimeZone = new Date(
      currentDateAsEasternTimeZone.getFullYear(),
      currentDateAsEasternTimeZone.getMonth(),
      currentDateAsEasternTimeZone.getDate(),
      0,
      0,
      0,
    )
    const firstPaymentOnDate = new Date(`${getValues('firstPaymentOn')}T00:00:00`)

    try {
      if (
        isNFl
          ? computedInfo.effectiveRate > highCostRateForProvince
          : computedInfo.effectiveRate > financingConfig.effectiveRateThreshold
      ) {
        dispatch(
          appActions.setBusinessErrors([
            {
              message: t('worksheetCommon.effectiveRateTooHigh', '', {
                effectiveRate: computedInfo.effectiveRate,
                maxRate: isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold,
              }),
            },
          ]),
        )
      } else if (compareAsc(deliveryOnDateAsEasternTimeZone, baseActivationDateAsEasternTimeZone) === -1) {
        dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.activationDateInPast' }]))
      } else if (amountRequested > availableCredit) {
        dispatch(
          appActions.setBusinessErrors([{ message: 'worksheetCommon.amountRequestedHigherThanAvailableCredit' }]),
        )
      } else if (compareAsc(deliveryOnUTC, receivedOnPlus90Days) === 1)
        dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.activationDateReport90Days' }]))
      else if (compareAsc(deliveryOnUTC, expiry) === 1)
        dispatch(appActions.setBusinessErrors([{ message: 'worksheetCommon.deliveryOnEarlierExpiration' }]))
      else if (minFirstPaymentDate > firstPaymentOnDate) {
        dispatch(
          appActions.setBusinessErrors([
            {
              message: t('worksheetCommon.firstPaymentDateInPast', {
                minFirstPaymentDate: formatDate(minFirstPaymentDate),
              }),
            },
          ]),
        )
      } else if (maxFirstPaymentDate < firstPaymentOnDate) {
        dispatch(
          appActions.setBusinessErrors([
            {
              message: t('worksheetCommon.firstPaymentDateTooFar', {
                maxFirstPaymentDate: formatDate(maxFirstPaymentDate),
              }),
            },
          ]),
        )
      } else {
        data.amountRequested = amountRequested
        data.status = EWorksheetStatus.Active
        await dispatchEffect(ifXpressWorksheetEffects.update(data)).then(() =>
          dispatchEffect(fullCreditApplicationEffects.getById(creditApplication!.id, EFinancingProgram.IfXpress)).then(
            () =>
              navigate(`/Applications/${EFinancingProgram.IfXpress}/${creditApplication!.id}/view`, { replace: true }),
          ),
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const { minFirstPaymentDate, maxFirstPaymentDate } = useFirstPaymentDateOptions({
    financingProgramId: creditApplication?.financingProgramId,
    creditApplicationId: creditApplication?.id,
    deliveryOn,
    paymentFrequency,
    isHighEstimate: ifXpressWorksheet?.isHighEstimate ?? false,
    setValue,
    getValues,
  })

  const handleDeletingPrivateMessage = (message: CreditApplicationMessage) => {
    if (creditApplication?.id) {
      return dispatchEffect(messageEffects.deleteMessage(message, creditApplication.financingProgramId))
    }
    return null
  }

  return (
    <div>
      <PageSpinner withBackdrop isLoading={(isLoading && isLoadingCreditApplication) || isSubmitting} />

      {!isLoading && (
        <Stack>
          <PageError errors={error} />
          {creditApplication && aprTooHigh && (
            <Alert severity="error">
              {t('common.errors.aprTooHigh', '', {
                threshold: creditApplication.applicant.currentAddress.stateIso
                  ? aprThresholds[creditApplication.applicant.currentAddress.stateIso]
                  : '',
                stateIso: creditApplication.applicant.currentAddress.stateIso
                  ? t(`EProvince.${creditApplication.applicant.currentAddress.stateIso}`, { ns: 'enum' })
                  : '',
              })}
            </Alert>
          )}
        </Stack>
      )}
      <Breadcrumb trees={breadCrumbs} />
      <form onSubmit={handleSubmit(onFormSubmitted, reportErrorToConsole)}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <InputTextField
            value={t(`EPlanType.${planName}`, { ns: 'enum' })}
            label={t('worksheetCommon.program')}
            disabled
          />
          <InputTextField disabled value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0} />
          <AvailableCreditTooltip availableCreditData={availableCreditData}>
            <div style={{ position: 'relative' }}>
              <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
              {creditInProgress !== 0 && (
                <ErrorOutline
                  sx={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: theme.palette.error.main,
                    fontSize: '1.5rem',
                  }}
                />
              )}
            </div>
          </AvailableCreditTooltip>
          <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />

          <div style={{ marginTop: '1%' }}>
            <Button
              variant="contained"
              disabled={aprTooHigh || isComputingFunding || !computedInfo || isLoading}
              sx={{ width: 'auto', whiteSpace: 'nowrap', textAlign: 'left' }}
              onClick={handleSave}
            >
              {t('common.save')}
            </Button>
          </div>
        </Stack>
        {creditApplication && (
          <PrivateMessage
            messages={privateMessages}
            handleDeletingPrivateMessage={handleDeletingPrivateMessage}
            currentUser={user}
            creditAppCreationDate={creditApplication.createdOn}
          />
        )}
        {merchant && <MerchantPaymentInstructions merchant={merchant} />}
        <Grid container marginTop={5}>
          <Grid size={8}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <InputTextField
                  slotProps={{
                    input: {
                      endAdornment: '$',
                    },
                  }}
                  {...register('amountRequested')}
                  error={errors?.amountRequested}
                  label={t('worksheetCommon.amountRequested')}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <ControlledStringDateOnlyPicker
                  name="deliveryOn"
                  control={control}
                  label={t('worksheetCommon.activationDate')}
                  disablePast
                  minDate={currentDateAsEasternTimeZone}
                  disabled={creditApplication?.editLocked}
                  fullWidth
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <ControlledStringDateOnlyPicker
                  name="firstPaymentOn"
                  control={control}
                  label={t('worksheetCommon.firstPaymentDate')}
                  disablePast
                  minDate={minFirstPaymentDate}
                  maxDate={maxFirstPaymentDate}
                  fullWidth
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={paymentPlanList}
                  label={t('worksheet.paymentPlan')}
                  {...register('paymentPlanId')}
                  error={errors?.paymentPlanId}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={[{ label: t('EPaymentFrequency.monthly', { ns: 'enum' }), value: EPaymentFrequency.Monthly }]}
                  label={t('worksheet.paymentFrequency')}
                  {...register('paymentFrequency')}
                  error={errors?.paymentFrequency}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  disabled={isVariableInterest}
                  items={possibleTerms.map((item) => ({
                    label: `${item} ${t('worksheet.month')}`,
                    value: item,
                  }))}
                  label={t('worksheet.term')}
                  {...register('term')}
                  error={errors?.term}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <FormControlLabel
                  {...register('includeInsurance')}
                  control={<Checkbox defaultChecked={ifXpressWorksheet?.includeInsurance} />}
                  label={t('worksheetCommon.addProtectionPlan')}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={4}>
            <Box
              sx={{
                border: 1,
                padding: 2,
                marginLeft: 5,
                paddingBottom: 10,
              }}
            >
              <Stack direction="column" spacing={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('worksheetCommon.fundingSummary')} :
                </Typography>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.loanAmount')} :</Typography>
                  <Typography>{`${formatCurrency(getValues('amountRequested'))}`}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>
                    {`${t('worksheetCommon.interest')} (${
                      isVariableInterest
                        ? formatPaymentPlanForSummary(selectedPlan)
                        : `${creditApplication?.finalCreditDecision?.interestRate}%`
                    })`}
                    :{' '}
                  </Typography>
                  <Typography>{formatCurrency(computedInfo?.totalInterestAmount)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{`${t('worksheetCommon.financeFee')} (${computedInfo?.lenderFeeRate}%)`}:</Typography>
                  <Typography>{formatCurrency(totalFees)}</Typography>
                </Stack>

                {isVariableInterest && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography>
                      {`${t('worksheetCommon.merchantFee')} (${selectedPlan?.merchantFeeRate}%)`} :
                    </Typography>
                    <Typography>{getMerchantFees()}</Typography>
                  </Stack>
                )}

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.protectionPlan')} :</Typography>
                  <Typography>{formatCurrency(totalInsurance)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.totalObligation')} :</Typography>
                  <Typography>{formatCurrency(totalObligation)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.annualPercentageRate')} :</Typography>
                  <Typography>{computedInfo.effectiveRate} %</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" paddingTop={3}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('worksheetCommon.payment')}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.maxPmtAmount')} :</Typography>
                  <Typography>{formatCurrency(creditApplication?.finalCreditDecision.maxPmtAmount)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.paymentFrequency')} :</Typography>
                  <Typography>
                    {ifXpressWorksheet?.paymentFrequency
                      ? t(`EPaymentFrequency.${ifXpressWorksheet?.paymentFrequency}`, { ns: 'enum' })
                      : t('EPaymentFrequency.monthly', { ns: 'enum' })}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.frequencyAmount')} :</Typography>
                  <Typography>{formatCurrency(computedInfo?.paymentForFrequency)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t('worksheetCommon.firstPaymentDate')} :</Typography>
                  <Typography>{formatDate(firstPaymentOn)}</Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between" paddingTop={3}>
                  <Typography fontWeight="bold">{t('worksheetCommon.amountToBePaid')} :</Typography>
                  <Typography>{formatCurrency(getValues('amountRequested') ?? 0)}</Typography>
                </Stack>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </form>
    </div>
  )
}
export default EditIfXpressWorksheetPage
